import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, type ReactElement } from 'react';
import { isApiError, newClientId, sendTemplateMessage } from '../../api';
import { conversationKey, invalidateConversationLists } from '../conversation';
import {
  DialogFooter,
  Modal,
  TemplateChooser,
  templateErrorMessage,
  useTemplatePicker,
  useTemplates,
} from '../templates';
import forms from '../admin/forms.module.css';
import { addMessageToCache } from './messages-cache';

/** O que a janela diz: fechada, o modelo é o único jeito de falar; aberta, é uma escolha (e o histórico fica junto). */
export const TEMPLATE_DESCRIPTION_CLOSED =
  'Fora da janela de 24 horas só vale um modelo aprovado. Quando o cliente responder, você volta a poder escrever livremente.';
export const TEMPLATE_DESCRIPTION_OPEN =
  'O cliente recebe um modelo de mensagem aprovado, nesta mesma conversa: ele fica no histórico junto com as outras mensagens.';

/**
 * "Enviar modelo" numa conversa que já existe: o cliente recebe um modelo aprovado nesta mesma conversa. Com a
 * janela de 24 h fechada é o único jeito de retomá-la (ao responder, o cliente a reabre); com ela aberta também
 * vale. O texto montado entra na conversa assim que o servidor aceita; o estado (enviado, entregue, lido) chega
 * pelo tempo real, como em qualquer mensagem.
 */
export function SendTemplateDialog({
  conversationId,
  lineId,
  windowClosed,
  onClose,
}: {
  conversationId: string;
  /** O número em que a conversa acontece: os modelos são os da conta dele (`undefined` = a conta principal). */
  lineId?: string | undefined;
  /** A janela de resposta está fechada? Só muda o texto explicativo. */
  windowClosed: boolean;
  onClose: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const templates = useTemplates(lineId);
  const clientId = useRef(newClientId());
  const renewClientId = useCallback(() => {
    clientId.current = newClientId();
  }, []);
  const picker = useTemplatePicker(templates.data?.templates ?? [], renewClientId);
  const [formError, setFormError] = useState<string | null>(null);

  const mutation = useMutation({ mutationFn: sendTemplateMessage });
  const pending = mutation.isPending;

  const submit = (): void => {
    if (pending) return;
    setFormError(null);
    const template = picker.validate();
    if (!template) return;
    mutation.mutate(
      { conversationId, template, clientId: clientId.current },
      {
        onSuccess: (message) => {
          addMessageToCache(queryClient, conversationId, message);
          void invalidateConversationLists(queryClient);
          void queryClient.invalidateQueries({ queryKey: conversationKey(conversationId) });
          onClose();
        },
        onError: (error) => {
          if (isApiError(error) && error.code === 'template_not_found') void templates.refetch();
          setFormError(templateErrorMessage(error));
        },
      },
    );
  };

  return (
    <Modal
      title="Enviar modelo de mensagem"
      description={windowClosed ? TEMPLATE_DESCRIPTION_CLOSED : TEMPLATE_DESCRIPTION_OPEN}
      onClose={onClose}
      busy={pending}
    >
      <form
        className={forms.form}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <TemplateChooser templates={templates} picker={picker} busy={pending} />
        <DialogFooter
          error={formError}
          pending={pending}
          submitLabel="Enviar modelo"
          onCancel={onClose}
        />
      </form>
    </Modal>
  );
}
